import { t } from "@lingui/macro";
import { useState } from "react";
import { Button, Input } from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  ExportIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  GiftIcon,
  CoinIcon,
  UserCheckIcon,
} from "@phosphor-icons/react";
import { useBonus } from "@/client/services/bonus-ref/bonus";
import { useFilterBonusRef } from "@/client/stores/filter";
import { useDialog } from "@/client/stores/dialog";
import { useExportBonus } from "@/client/services/bonus-ref/export-bonus";
import qs from "qs";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";

export const BonusAdminPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { resetFilter, filter, replaceFilter } = useFilterBonusRef();
  const { bonus, error } = useBonus(page, filter);
  const { open } = useDialog("filter-bonus");
  const { refetch } = useExportBonus(qs.stringify(filter));

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecficpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter = { ...filter };
    delete nextFilter.userId;

    if (search.trim()) {
      nextFilter.userId = { contains: search.trim() };
    }

    setPage(1);
    replaceFilter(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setPage(1);
    resetFilter();
  };

  // Colonnes basées sur le contexte Bonus (Utilisateur, Montant, Date, Statut)
  const columns: Column<any>[] = [
    {
      key: "userId",
      header: "Utilisateur",
      render: (item) => (
        <span className="font-medium text-admin-text-primary">{item.userId || "—"}</span>
      ),
    },
    {
      key: "amount",
      header: "Montant",
      render: (item) => (
        <span className="text-admin-text-secondary">
          {item.amount} {item.currency || ""}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Date",
      render: (item) => (
        <span className="text-admin-text-secondary">
          {new Date(item.createdAt).toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <>
      {/* Header de page */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">Bonus de Parrainage</h1>
          <p className="mt-1 text-sm text-admin-text-muted">{t`Tableau de bord / Bonus de Parrainage`}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher des bonus…`}
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Notifications"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="h-8 w-8 overflow-hidden rounded-full bg-admin-primary">
            <img src="/avatar.jpg" alt={t`Utilisateur`} className="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Bonus affichés"
              number={bonus?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <GiftIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 4.2%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Bonus actifs"
              number={bonus?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <CoinIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 3.7%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Page actuelle"
              number={page.toString()}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <UserCheckIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-accent">● Live</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`mis à jour`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Pages totales"
              number={bonus?.page?.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="bg-admin-warning/20 text-admin-warning rounded-full p-3">
              <GiftIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-admin-warning text-xs">▲ 1.3%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>
      </div>

      {/* Section Filtres et Actions */}
      <div className="mb-8 rounded-xl-admin border border-admin-border bg-admin-surface p-6">
        <div className="flex flex-col flex-wrap justify-between gap-4 lg:flex-row">
          {/* Recherche */}
          <div className="relative min-w-[250px] flex-1">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher par identifiant utilisateur…`}
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          {/* Filtres Selects */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-end gap-3">
              <Button
                variant="outline"
                onClick={applyQuickFilters}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Filter
              </Button>
              <Button
                variant="outline"
                onClick={() => open("create", { id: "filter-bonus" })}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Advanced filters
              </Button>
              <Button
                variant="ghost"
                onClick={resetFilters}
                className="h-10 text-admin-text-muted hover:text-admin-text-primary"
              >
                Reset
              </Button>
              <Button
                variant="outline"
                onClick={() => refetch()}
                className="h-10 bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
              >
                <ExportIcon size={16} className="mr-2" /> Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau & Pagination */}
      <DataTable
        title=""
        data={bonus?.data ?? []}
        columns={columns}
        pageSize={10}
        className="shadow-sm-admin"
      />

      {/* Pagination (Style UserAdminPage) */}
      <div className="my-3 flex flex-row justify-end space-x-3">
        {page > 1 && (
          <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
            <CaretCircleLeftIcon size={32} weight="regular" />
          </Button>
        )}
        {page - 2 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 2)}
            className="rounded-lg"
          >
            {page - 2}
          </Button>
        )}
        {page - 1 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 1)}
            className="rounded-lg"
          >
            {page - 1}
          </Button>
        )}

        <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
          {page}
        </Button>
        {bonus && page + 1 <= bonus.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 1)}
            className="rounded-lg"
          >
            {page + 1}
          </Button>
        )}
        {bonus && page + 2 <= bonus.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 2)}
            className="rounded-lg"
          >
            {page + 2}
          </Button>
        )}
        {bonus && page < bonus.page && (
          <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
            <CaretCircleRightIcon size={32} weight="regular" />
          </Button>
        )}
      </div>
    </>
  );
};
